import {
  Box,
  Button,
  alpha,
  Hidden,
  Typography,
  useTheme,
} from "@material-ui/core";
import { graphql } from "gatsby";
import React, { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { useWindowScroll } from "react-use";
import LocalizedLink from "../../components/localized-link";
import useReferralCodeParam from "../../containers/referral/hooks/useReferralCodeParam";
import { convertDatoCmsAssetUrlToCloudfrontUrl } from "../../helpers/cdn";
import { formatMultilinesText } from "../../helpers/markdown";
import { useAuthContextSelector } from "../../hooks/use-auth";
import { useExpressDeliveryAvailabilitySelector } from "../../hooks/use-express-delivery-availability";
import useExpressDeliveryCart from "../../hooks/use-express-delivery-cart";
import useIsMobile from "../../hooks/use-is-mobile";
import useIsMounted from "../../hooks/use-ismounted";
import { BackgroundVideoWrapper, CustomHeading, VideoGradient } from "./styles";
import { ReferralInviteFragment as Props } from "./__generated__/ReferralInviteFragment";
import { buildURL } from "react-imgix";

function HomepageHero({
  title,
  subtitle,
  ctaMode,
  ctaDescription,
  ctaLabel,
  backgroundVideo,
  backgroundVideoMobile,
  backgroundPoster,
  backgroundPosterMobile,
}: Props) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const hasActiveMembershipSubscription = useAuthContextSelector(
    (c) => c.hasActiveMembershipSubscription
  );
  const address = useExpressDeliveryAvailabilitySelector((c) => c.address);
  const isAddressPanelOpen = useExpressDeliveryAvailabilitySelector(
    (c) => c.isAddressPanelOpen
  );

  const { items: cartItems } = useExpressDeliveryCart();
  const isMounted = useIsMounted();
  const referralCodeParam = useReferralCodeParam();

  /**
   * Special video behaviour:
   * 1. Play video once, then stop.
   * 2. After user scroll down, reset video to begining
   * 3. When user scroll back top, play video again then stop.
   */
  const desktopVideoRef = useRef<HTMLVideoElement>();
  const mobileVideoRef = useRef<HTMLVideoElement>();
  const { y: windowScrollY } = useWindowScroll();

  useEffect(() => {
    const videoRef = isMobile ? mobileVideoRef : desktopVideoRef;
    if (!videoRef.current) return;

    const videoHeight = videoRef.current.offsetHeight;
    const videoIsPlaying = videoRef.current.currentTime > 0;
    const videoIsReset = videoRef.current.currentTime === 0;

    // User scroll past video; reset video to begining
    if (!videoIsReset && windowScrollY > videoHeight) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    //  User scroll back to top of window; play video again
    if (!videoIsPlaying && windowScrollY === 0) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      // Older browsers may not return a value from play()
      const promise = videoRef.current.play();
      if (promise) {
        promise.catch((e) => {});
      }
    }
  }, [windowScrollY]);

  const desktopVideoPoster =
    buildURL(backgroundPoster?.url, { w: 1920, q: 75 }) ||
    `${backgroundVideo.video.thumbnailUrl}?time=0`;
  const mobileVideoPoster =
    buildURL(backgroundPosterMobile?.url, { w: 600, q: 75 }) ||
    `${backgroundVideoMobile.video.thumbnailUrl}?time=0`;

  /**
   * Sticky CTA behaviour
   */
  const [ctaRef, isCtaInView] = useInView({
    threshold: 0,
    delay: 500,
  });
  const showStickyBars =
    !isCtaInView && windowScrollY > 100 && !hasActiveMembershipSubscription;

  const isExpressEnabled =
    !!address || cartItems?.length > 0 || isAddressPanelOpen;

  const ctaButton =
    hasActiveMembershipSubscription || ctaMode !== "invite" ? (
      <Button
        fullWidth
        variant="contained"
        component={LocalizedLink}
        to={"/shop"}
        data-analytics="homepage.hero.cta"
      >
        Shop now
      </Button>
    ) : (
      <Button
        fullWidth
        variant="contained"
        component={LocalizedLink}
        to={`/referral/${referralCodeParam}/register`}
        data-analytics="homepage.hero.cta"
      >
        {ctaLabel}
      </Button>
    );

  return (
    <>
      <BackgroundVideoWrapper
        className={isExpressEnabled ? "express-panel" : ""}
      >
        {/* TODO: FIND SOLUTION TO PREVENT LOADING MOBILE & DESKTOP VIDEO FILES */}

        {/* Mobile video */}
        <Hidden mdUp implementation="css">
          <video
            controls={false}
            preload="auto"
            autoPlay
            muted
            playsInline
            poster={mobileVideoPoster}
            ref={mobileVideoRef}
            // @ts-ignore
            disableRemotePlayback={true}
            style={{ backgroundColor: theme.palette.primary.main }}
          >
            <source
              src={convertDatoCmsAssetUrlToCloudfrontUrl(
                backgroundVideoMobile.url
              )}
              type={backgroundVideoMobile.mimeType}
            />
          </video>
        </Hidden>

        {/* Desktop video */}
        <Hidden smDown implementation="css">
          <video
            controls={false}
            preload="auto"
            autoPlay
            muted
            playsInline
            poster={desktopVideoPoster}
            ref={desktopVideoRef}
            // @ts-ignore
            disableRemotePlayback={true}
            style={{ backgroundColor: theme.palette.primary.main }}
          >
            <source
              src={convertDatoCmsAssetUrlToCloudfrontUrl(backgroundVideo.url)}
              type={backgroundVideo.mimeType}
            />
          </video>
        </Hidden>

        {/* Layer on top of video */}
        <Box
          zIndex={200}
          paddingX={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="100%"
        >
          {/* Title  */}
          <CustomHeading
            dangerouslySetInnerHTML={{
              __html: formatMultilinesText(title),
            }}
          />

          {/* Description */}
          {(subtitle || subtitle) && (
            <Box mt={2}>
              <Typography
                variant="h5"
                dangerouslySetInnerHTML={{
                  __html: subtitle,
                }}
                style={{
                  color: theme.palette.grey[100],
                  textAlign: "center",
                }}
              />
            </Box>
          )}

          <Box
            maxWidth="420px"
            display="flex"
            width="100%"
            mt={{ xs: 4, sm: 4 }}
            mb={!ctaDescription ? { xs: 2, sm: 4 } : 0}
            ref={ctaRef}
          >
            {ctaButton}
          </Box>

          {/* CTA Description */}
          {ctaDescription && (
            <Box
              maxWidth="420px"
              width="100%"
              mt={{ xs: 1.5, sm: 1.5 }}
              display="flex"
              justifyContent="center"
            >
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{
                  __html: formatMultilinesText(ctaDescription),
                }}
                className="center"
              />
            </Box>
          )}
        </Box>

        {/* Specuak gradient to fix coloring between video and homepage background */}
        <VideoGradient />
      </BackgroundVideoWrapper>

      {/* Mobile+Tablet sticky bar */}
      {!isExpressEnabled && isMounted && (
        <Box
          display={{ xs: "flex", sm: "none" }}
          position="fixed"
          width="100%"
          alignItems="center"
          height={theme.spacing(8)}
          top={0}
          left={0}
          paddingX={3}
          bgcolor={alpha(theme.palette.text.primary, 0.7)}
          style={{
            opacity: showStickyBars ? 1 : 0,
            pointerEvents: showStickyBars ? "all" : "none",
            transition: "opacity 250ms",
            backdropFilter: `blur(3px)`,
            WebkitBackdropFilter: "blur(3px)",
          }}
          zIndex={1200}
        >
          {ctaButton}
        </Box>
      )}
    </>
  );
}

export const ReferralInviteFragment = graphql`
  fragment ReferralInviteFragment on DatoCmsCModuleReferralInvite {
    title
    subtitle
    ctaMode
    ctaLabel
    ctaDescription
    container
    containerDisableGutters
    backgroundVideo {
      mimeType
      url
      video {
        thumbnailUrl(format: png)
      }
    }
    backgroundVideoMobile {
      mimeType
      url
      video {
        thumbnailUrl(format: png)
      }
    }
    backgroundPoster {
      url
    }
    backgroundPosterMobile {
      url
    }
    model {
      apiKey
    }
  }
`;

export default React.memo(HomepageHero);
